import { parseDate } from '@/utils/format'

// Statystyki szczytów liczone z /api/ascents (lista wejść użytkownika).

function withDates(ascents) {
  return ascents.map(ascent => ({ ...ascent, date: parseDate(ascent.time) }))
}

const byDateDesc = (a, b) => (b.date ? b.date.getTime() : -Infinity) - (a.date ? a.date.getTime() : -Infinity)

// Unikalne szczyty (po location_id) z ostatnim wejściem
export function uniquePeaks(ascents) {
  const peaks = new Map()
  ascents.forEach(ascent => {
    const id = ascent.location_id
    const date = ascent.date !== undefined ? ascent.date : parseDate(ascent.time)
    const existing = peaks.get(id)
    if (!existing) {
      peaks.set(id, {
        id,
        name: ascent.location_name,
        elevation: Number(ascent.location_elevation) || 0,
        coords: ascent.coords || null,
        count: 1,
        firstDate: date,
        lastDate: date
      })
      return
    }
    existing.count += 1
    if (date && (!existing.lastDate || date > existing.lastDate)) existing.lastDate = date
    if (date && (!existing.firstDate || date < existing.firstDate)) existing.firstDate = date
  })
  return [...peaks.values()]
}

// Podsumowanie dla dashboardu i karty share. Okres = bieżący rok; jeśli w tym
// roku nie ma jeszcze wejść, pokazujemy całą historię (periodYear = null).
export function summarizeAscents(ascents = [], now = new Date()) {
  const all = withDates(ascents)
  const year = now.getFullYear()
  const inYear = all.filter(ascent => ascent.date && ascent.date.getFullYear() === year)
  const period = inYear.length ? inYear : all
  const periodYear = inYear.length ? year : null

  const peaks = uniquePeaks(period)
  const elevationSum = peaks.reduce((sum, peak) => sum + peak.elevation, 0)
  const highest = peaks.reduce((top, peak) => (!top || peak.elevation > top.elevation ? peak : top), null)

  const monthStart = new Date(year, now.getMonth(), 1)
  const monthPeaks = uniquePeaks(all.filter(ascent => ascent.date && ascent.date >= monthStart))

  return {
    hasAscents: all.length > 0,
    periodYear,
    peakCount: peaks.length,
    ascentCount: period.length,
    elevationSum,
    avgElevation: peaks.length ? elevationSum / peaks.length : 0,
    monthPeakCount: monthPeaks.length,
    highest,
    // najwyższe szczyty okresu (karta share)
    topPeaks: [...peaks].sort((a, b) => b.elevation - a.elevation),
    recentAscents: [...all].sort(byDateDesc),
    // mapa: wszystkie zdobyte szczyty z poprawnymi współrzędnymi (cała historia)
    mapPeaks: uniquePeaks(all).filter(peak => hasValidCoords(peak.coords))
  }
}

// Współrzędne z BE bywają null/puste — Number(null) = 0 dawało punkt 0°N 0°E,
// który rozciągał zakres mapy i ściskał prawdziwe szczyty w jednym rogu.
export function hasValidCoords(coords) {
  if (!coords || coords.lat === null || coords.lng === null || coords.lat === '' || coords.lng === '') return false
  const lat = Number(coords.lat)
  const lng = Number(coords.lng)
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return false
  return !(lat === 0 && lng === 0)
}

const quantile = (sorted, q) => sorted[Math.min(sorted.length - 1, Math.max(0, Math.round(q * (sorted.length - 1))))]

// Zakres mapy [[minLng, minLat], [maxLng, maxLat]] odporny na pojedyncze odległe
// szczyty: przy większej liczbie punktów odrzucamy szczyty spoza 5–95% na którejś
// osi, a zakres liczymy z pozostałych. (Osobne obcinanie osi dawało pusty róg
// kadru: szerokość z jednego odległego szczytu, długość z innego.) trim: 0 = wszystkie.
export function peaksBounds(peaks, { trim = 0.05 } = {}) {
  if (!peaks.length) return null
  const points = peaks.map(peak => [Number(peak.coords.lng), Number(peak.coords.lat)])
  let kept = points
  if (peaks.length >= 10 && trim > 0) {
    const lngs = points.map(([lng]) => lng).sort((a, b) => a - b)
    const lats = points.map(([, lat]) => lat).sort((a, b) => a - b)
    const [minLng, maxLng] = [quantile(lngs, trim), quantile(lngs, 1 - trim)]
    const [minLat, maxLat] = [quantile(lats, trim), quantile(lats, 1 - trim)]
    const inside = points.filter(([lng, lat]) => lng >= minLng && lng <= maxLng && lat >= minLat && lat <= maxLat)
    if (inside.length) kept = inside
  }
  const lngs = kept.map(([lng]) => lng)
  const lats = kept.map(([, lat]) => lat)
  return [
    [Math.min(...lngs), Math.min(...lats)],
    [Math.max(...lngs), Math.max(...lats)]
  ]
}

// Rzutowanie punktów (lat/lng) do prostokąta w pikselach z zachowaniem proporcji.
// Zakres wyznacza peaksBounds(), więc odległe pojedyncze szczyty wypadają poza kadr.
export function projectPeaks(peaks, { width, height, padding = 24 }) {
  const valid = peaks.filter(peak => hasValidCoords(peak.coords))
  const bounds = peaksBounds(valid)
  if (!bounds) return []
  const [[minLng, minLat], [maxLng, maxLat]] = bounds
  const scaleX = Math.cos((((minLat + maxLat) / 2) * Math.PI) / 180) || 1
  const points = valid.map(peak => ({
    peak,
    x: Number(peak.coords.lng) * scaleX,
    y: -Number(peak.coords.lat)
  }))
  const minX = minLng * scaleX
  const maxX = maxLng * scaleX
  const minY = -maxLat
  const maxY = -minLat
  const spanX = maxX - minX
  const spanY = maxY - minY
  const innerW = width - 2 * padding
  const innerH = height - 2 * padding
  const scale = Math.min(spanX ? innerW / spanX : Infinity, spanY ? innerH / spanY : Infinity)
  const k = Number.isFinite(scale) ? scale : 0
  const offsetX = (width - spanX * k) / 2
  const offsetY = (height - spanY * k) / 2
  return points
    .map(point => ({
      peak: point.peak,
      x: offsetX + (point.x - minX) * k,
      y: offsetY + (point.y - minY) * k
    }))
    .filter(point => point.x >= 0 && point.x <= width && point.y >= 0 && point.y <= height)
}
